import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toValue } from 'vue';
import type { MutationDefaultsContext } from './query-client';
import { paymentTerminalsService } from '@/api/payment-terminals.service';
import type {
  CreatePaymentTerminalDto,
  FindAllPaymentTerminalsQueryDto,
  PaymentTerminal,
  UpdatePaymentTerminalDto,
} from '@/entities/payment-terminals/payment-terminal.entity';
import type { ChargeCreditCardDto } from '@/entities/payment-terminals/charge-credit-card.dto';

const QUERY_KEY = 'payment-terminal';

export const usePaymentTerminal = (
  id: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => paymentTerminalsService.getOne(toValue(id)),
    enabled: () => toValue(enabled),
  });
};

export const usePaymentTerminalsWithQuery = (
  query: MaybeRefOrGetter<FindAllPaymentTerminalsQueryDto>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY, query],
    queryFn: ({ pageParam = 1 }) =>
      paymentTerminalsService.getAllWithQuery({
        ...toValue(query),
        page: pageParam,
      }),
    enabled: () => toValue(enabled),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useUpdatePaymentTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({
      id,
      paymentTerminal,
    }: {
      id: string;
      paymentTerminal: UpdatePaymentTerminalDto;
    }) => paymentTerminalsService.update(id, paymentTerminal),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Payment Terminal updated',
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      updateItemOnQueriesData<PaymentTerminal>([QUERY_KEY], result, queryClient);
    },
  });
};

export const useCreatePaymentTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (paymentTerminal: CreatePaymentTerminalDto) =>
      paymentTerminalsService.create(paymentTerminal),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Payment Terminal saved',
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      addItemToQueriesData<PaymentTerminal>([QUERY_KEY], result, queryClient);
    },
  });
};

export const useDeletePaymentTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (id: string) => paymentTerminalsService.remove(id),
    onMutate: (id) => ({ id, successMessage: 'Payment Terminal deleted' }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      removeItemFromQueriesData([QUERY_KEY], variables, queryClient);
    },
  });
};

export const useChargeCreditCard = () => {
  return useMutation({
    mutationKey: ['charge-credit-card'],
    mutationFn: (data: ChargeCreditCardDto) => paymentTerminalsService.chargeCreditCard(data),
    onMutate: () => ({ showSuccessToast: false }),
  });
};
